import { useAuthV2 } from "./store";
import { refresh as callRefresh, type AuthError } from "./api";
import { STAFF_INACTIVE_CODE } from "./staffInactive";

let inFlightRefresh: Promise<boolean> | null = null;
let lastSuccessfulRefreshAt = 0;

const authMode = import.meta.env.VITE_AUTH_MODE ?? "cookie-session";
export const SESSION_REQUEST_REFRESH_MIN_GAP_MS = 5 * 60 * 1000;

const temporaryRefreshErrorCodes = new Set([
  "provider_temporarily_unavailable",
  "branch_catalog_unavailable",
]);

function isTemporaryRefreshError(error: AuthError): boolean {
  return error.status === 503 || temporaryRefreshErrorCodes.has(error.code ?? "");
}

async function refreshSession(): Promise<boolean> {
  // Whose session this refresh was started for. A refresh in flight when the
  // user switches accounts resolves with the OLD account's identity, and
  // writing that back would silently replace the account they just picked —
  // the store would say "photo" while the cookie says "makeup". Empty means
  // the refresh started before any identity was known (hard reload), which
  // any result may legitimately fill in.
  const startedFor = useAuthV2.getState().casdoorSub;
  const stillSameAccount = () => {
    const now = useAuthV2.getState().casdoorSub;
    return startedFor === null || now === null || now === startedFor;
  };
  try {
    const out = await callRefresh();
    if (!stillSameAccount()) return true;
    // Use setSession so casdoor_sub + display_name are kept in sync — the
    // first /auth/refresh after a hard refresh in a new tab is the only path
    // that restores these fields without re-running the OAuth dance.
    useAuthV2.getState().setSession({
      accessToken: out.access_token ?? null,
      casdoorSub: out.casdoor_sub,
      displayName: out.display_name,
      staff: out.staff,
      permissions: out.permissions ?? [],
      branchScope: out.branchScope,
      dataVisibility: out.dataVisibility,
      persona: out.persona,
    });
    markSessionSnapshotFresh();
    return true;
  } catch (error) {
    const authError = error as AuthError;
    // A stale refresh failing says nothing about the account that replaced it,
    // so it must not clear the fresh session either.
    if (!isTemporaryRefreshError(authError) && stillSameAccount()) {
      // QĐ 0412: đã nghỉ thì mang câu máy chủ nói sang trang đăng nhập.
      if (authError.code === STAFF_INACTIVE_CODE) useAuthV2.getState().signOut(authError.message);
      else useAuthV2.getState().clear();
    }
    return false;
  }
}

function getRefreshPromise(): Promise<boolean> {
  if (!inFlightRefresh) {
    inFlightRefresh = refreshSession().finally(() => {
      inFlightRefresh = null;
    });
  }
  return inFlightRefresh;
}

// ensureSessionRefreshed drives a single shared /auth/refresh and hydrates the
// store on success (clears it on failure). Every caller — the _app bootstrap
// on hard reload AND the 401 interceptor — funnels through the same in-flight
// promise, so concurrent callers never trigger two Casdoor refresh-token
// rotations. That double rotation is fatal: Casdoor treats the second use of
// the now-consumed token as reuse and rejects it, and the BE responds by
// revoking every session for the sub — which logs the user out AND drops their
// device PIN enrolment, forcing a full Casdoor re-signin + new PIN. React
// StrictMode double-invokes the bootstrap effect in dev, and in prod the
// bootstrap can still race the first protected call's 401 — both are collapsed
// here. Returns true when the session is now valid.
export function ensureSessionRefreshed(): Promise<boolean> {
  return getRefreshPromise();
}

export function markSessionSnapshotFresh(now = Date.now()): void {
  lastSuccessfulRefreshAt = now;
}

export async function ensureFreshSessionForRequest(now = Date.now()): Promise<void> {
  if (authMode !== "cookie-session") return;
  if (!useAuthV2.getState().casdoorSub) return;
  if (inFlightRefresh) {
    await getRefreshPromise();
    return;
  }
  if (now - lastSuccessfulRefreshAt < SESSION_REQUEST_REFRESH_MIN_GAP_MS) return;
  await getRefreshPromise();
}

export function resetRefreshStateForTests(): void {
  inFlightRefresh = null;
  lastSuccessfulRefreshAt = 0;
}

// refreshOn401 takes an already-fetched Response. If it's a 401, attempt a
// /auth/refresh; on success, update the store and re-execute the original
// request via the supplied `retry` callback. Terminal failures clear the
// store; temporary provider failures preserve it for a later retry. Either
// way, return the original 401 unchanged when refresh did not succeed.
export async function refreshOn401(
  res: Response,
  retry: () => Promise<Response>,
): Promise<Response> {
  if (res.status !== 401) return res;
  const refreshed = await getRefreshPromise();
  if (!refreshed) return res;
  return retry();
}
